import { Box } from '@ltvco/refresh-lib/theme';
import { useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import {
  COURT_CASES_V2_ROUTES,
  LEGAL_INTELLIGENCE_ROUTES,
  parseLegalIntelSection,
  withLegalIntelSection,
} from '../../../constants';
import { JudgeDetails } from '../../JudgeSearch';
import {
  useCourtOpinionDetails,
  CourtCaseDisclaimer,
  InlineLoading,
  ErrorStateContainer,
  StyledTabsNavigation,
  StyledTab,
} from '../../shared';
import { LegalSearchBreadcrumbs } from '../../shared/LegalSearchBreadcrumbs';

import { OpinionDetailsHeader } from './OpinionDetailsHeader';
import { OpinionsList } from './OpinionsList';
import { SimilarCases } from './SimilarCases';

import type {
  CourtCaseCluster,
  CourtOpinion,
  DisplayOpinion,
} from '../../types';

function getJudgeIdForOpinion(
  assignedToIdFromUrl: string | null,
  caseData: CourtCaseCluster | null,
  opinionsData: DisplayOpinion[] | undefined
): string | null {
  if (assignedToIdFromUrl && assignedToIdFromUrl !== 'null') {
    return assignedToIdFromUrl;
  }
  const firstAuthorId = opinionsData?.find(
    (o): o is CourtOpinion =>
      'author_id' in o && o.author_id !== null && o.type !== 'headmatter'
  )?.author_id;
  if (firstAuthorId !== undefined && firstAuthorId !== null) {
    return String(firstAuthorId);
  }
  const panel = caseData?.panel;
  if (
    Array.isArray(panel) &&
    panel.length > 0 &&
    typeof panel[0] === 'number'
  ) {
    return String(panel[0]);
  }
  return null;
}

export const OpinionDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const result = useCourtOpinionDetails(id);
  const { clusterQuery, opinionsQuery, similarCasesQuery, isLoading } = result;
  const [searchParams] = useSearchParams();
  const assigned_to_id = searchParams.get('assigned_to_id');

  const {
    data: caseData,
    isError: isCaseError,
    error: caseError,
  } = clusterQuery;
  const {
    data: opinionsData,
    isLoading: isOpinionsLoading,
    isError: isOpinionsError,
  } = opinionsQuery;
  const {
    data: similarCasesData,
    isLoading: isSimilarCasesLoading,
    isError: isSimilarCasesError,
  } = similarCasesQuery;

  if (isLoading) {
    return <InlineLoading />;
  }

  if (isCaseError || !caseData) {
    return (
      <ErrorStateContainer>
        <p>
          Error:{' '}
          {caseError instanceof Error ? caseError.message : 'Case not found'}
        </p>
      </ErrorStateContainer>
    );
  }

  const judgeId = getJudgeIdForOpinion(assigned_to_id, caseData, opinionsData);

  const tabs = [
    {
      label: 'Opinion',
      content: (
        <OpinionsList
          clusterOpinions={opinionsData || []}
          error={isOpinionsError ? 'Failed to load opinions' : undefined}
          isLoading={isOpinionsLoading}
        />
      ),
    },
    {
      label: `Similar Cases (${
        isSimilarCasesLoading ? '...' : similarCasesData?.count || 0
      })`,
      content: (
        <SimilarCases
          similarCases={similarCasesData || null}
          caseName={caseData.case_name_short || caseData.case_name || ''}
          error={
            isSimilarCasesError ? 'Failed to load similar cases' : undefined
          }
          isLoading={isSimilarCasesLoading}
        />
      ),
    },
    ...(judgeId
      ? [{ label: 'Judge', content: <JudgeDetails judgeId={judgeId} /> }]
      : []),
  ];

  const opinionBreadcrumbTitle =
    caseData.case_name_short?.trim() ||
    caseData.case_name?.trim() ||
    `Opinion ${id}`;

  const section = parseLegalIntelSection(location.search, location.pathname);

  return (
    <Box>
      <LegalSearchBreadcrumbs
        segments={[
          { label: 'Legal Intelligence', to: LEGAL_INTELLIGENCE_ROUTES.BASE },
          {
            label: 'Opinions',
            to: withLegalIntelSection(COURT_CASES_V2_ROUTES.OPINIONS, section),
          },
          { label: opinionBreadcrumbTitle },
        ]}
      />
      <OpinionDetailsHeader selectedCase={caseData} />

      <StyledTabsNavigation
        externalActiveTab={activeTab}
        onTabChanged={(newTab: number) => setActiveTab(newTab)}
        tab={StyledTab}
        tabs={tabs}
      />
      <CourtCaseDisclaimer />
    </Box>
  );
};
